import { alpha, type SxProps, type Theme } from '@mui/material';
import type { SkinStylesMap } from '../../hooks/useSkinStyles';
import { SKIN } from '../../types/enums';

type JackpotWinnerAnimationSkinStyles = {
  amount: SxProps<Theme>;
  divider: SxProps<Theme>;
  fallbackArtworkSrc: string;
  /** Transparent celebration video. */
  videoSrc: string;
  label: SxProps<Theme>;
  prizeFrame: SxProps<Theme>;
  prizeSurface: SxProps<Theme>;
  tierName: SxProps<Theme>;
};

// A subtle neutral sheen from the top edge that fades into the base surface colour.
const prizeSurfaceGradient = (theme: Theme) =>
  `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.06)} 0%, transparent 70%)`;

const NEOBET_WINNER_STYLES: JackpotWinnerAnimationSkinStyles = {
  amount: (theme) => ({ color: theme.palette.primary.main }),
  divider: (theme) => ({ backgroundColor: alpha(theme.palette.primary.main, 0.3) }),
  fallbackArtworkSrc: '/icons/jackpot-winner-purple.png',
  videoSrc: '/images/casino/jackpot/winner/overlay.webm',
  label: (theme) => ({ color: theme.palette.text.secondary }),
  prizeFrame: (theme) => ({
    borderColor: alpha(theme.palette.primary.main, 0.52),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)}, ${theme.palette.neutral[900]} 48%)`,
    boxShadow: `0 20px 48px ${alpha(theme.palette.common.black, 0.48)}`,
  }),
  prizeSurface: (theme) => ({
    borderColor: alpha(theme.palette.common.white, 0.09),
    backgroundColor: theme.palette.neutral[900],
    backgroundImage: prizeSurfaceGradient(theme),
  }),
  tierName: (theme) => ({ color: theme.palette.text.primary }),
};

const DEFAULT_WINNER_STYLES: JackpotWinnerAnimationSkinStyles = {
  fallbackArtworkSrc: '/icons/jackpot-winner-green.png',
  videoSrc: '/images/casino/jackpot/winner/overlay.webm',
  label: { color: 'text.secondary' },
  tierName: { color: 'text.primary' },
  amount: { color: 'info.main' },
  divider: (theme) => ({ backgroundColor: alpha(theme.palette.info.main, 0.25) }),
  prizeFrame: (theme) => ({
    borderColor: alpha(theme.palette.info.main, 0.35),
    backgroundColor: 'background.paper',
    boxShadow: `0 20px 48px ${alpha(theme.palette.common.black, 0.48)}`,
  }),
  prizeSurface: (theme) => ({
    borderColor: alpha(theme.palette.info.main, 0.18),
    backgroundColor: 'background.paper',
    backgroundImage: prizeSurfaceGradient(theme),
  }),
};

export const JACKPOT_WINNER_ANIMATION_SKIN_STYLES: SkinStylesMap<JackpotWinnerAnimationSkinStyles> = {
  [SKIN.BET365]: DEFAULT_WINNER_STYLES,
  [SKIN.GOLA]: DEFAULT_WINNER_STYLES,
  [SKIN.NEOBET]: NEOBET_WINNER_STYLES,
};

const EASE_OUT_EXPO = 'cubic-bezier(0.16, 1, 0.3, 1)';

// The card rises in first, its contents stagger in top to bottom, and the amount counts up once visible.
export const JACKPOT_WINNER_TIMING_MS = {
  card: { delay: 200, duration: 1000 },
  heading: 600,
  label: 700,
  tier: 800,
  divider: 950,
  amount: 1100,
  detailDuration: 800,
  shine: { delay: 1500, duration: 1600 },
  countUp: 5000,
  pop: 750,
} as const;

export const jackpotWinnerEntrance = (
  keyframes: string,
  delayMs: number,
  durationMs: number = JACKPOT_WINNER_TIMING_MS.detailDuration
) => ({
  animation: `${keyframes} ${durationMs}ms ${EASE_OUT_EXPO} ${delayMs}ms both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});
